/**
 * State behind the TP/SL modal's two advanced checkboxes and the trigger-source
 * selector.
 *
 * The three input VALUES live in react-hook-form (so their validation feeds the
 * same `errors` object that gates the Confirm button); only the three switches
 * are local. Everything is normalized here into the `desired` slice that
 * `TpslDesiredState` expects, so the submit path never has to know whether a
 * checkbox was on.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useWatch } from "react-hook-form";
import { GetAccount200ResponsePositionsInner } from "../../codegen-api";
import { TpslTriggersView } from "../../hooks/tpsl/tpsl.selectors";
import {
  normalizeDecimalString,
  truncateDecimalString,
} from "../../utils/decimalString";
import {
  DEFAULT_TPSL_PRICE_SOURCE,
  TpslPriceSource,
} from "../../hooks/tpsl/tpsl.types";
import {
  closeSizeName,
  slLimitPriceName,
  tpLimitPriceName,
} from "./TPSLForPositionModal.helpers";

interface UseTPSLAdvancedParams {
  position?: GetAccount200ResponsePositionsInner;
  /** Live triggers, used to restore the state of a leg that already exists. */
  triggers: TpslTriggersView;
  control: any;
  setValue: any;
  /** Raw trigger-price fields, used to seed a freshly enabled limit input. */
  tpTriggerPrice: any;
  slTriggerPrice: any;
  show?: boolean;
}

const positiveOrUndefined = (value: any): number | undefined => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

/**
 * The close size the submit path receives, as plain decimal text. A size at or
 * above the position is the position's own exact text: an explicit size, as
 * before, but never more than held and never through a float (`Number` of an
 * 18-decimal size can round a wei above the position).
 */
export const closeSizeForSubmit = (
  closeSize: unknown,
  positionAmount: string | number | undefined
): string | undefined => {
  const size = positiveOrUndefined(closeSize);
  if (size === undefined) return undefined;
  const heldText = truncateDecimalString(positionAmount, 18)?.replace(/^-/, "");
  if (heldText && Number(heldText) > 0 && size >= Number(heldText)) {
    return heldText;
  }
  return normalizeDecimalString(closeSize as string | number) ?? undefined;
};

export function useTPSLAdvanced({
  position,
  triggers,
  control,
  setValue,
  tpTriggerPrice,
  slTriggerPrice,
  show,
}: UseTPSLAdvancedParams) {
  const [configureAmount, setConfigureAmount] = useState(false);
  const [useLimitPrice, setUseLimitPrice] = useState(false);
  const [priceSource, setPriceSource] = useState<TpslPriceSource>(
    DEFAULT_TPSL_PRICE_SOURCE
  );

  const closeSize = useWatch({ control, name: closeSizeName });
  const tpLimitPrice = useWatch({ control, name: tpLimitPriceName });
  const slLimitPrice = useWatch({ control, name: slLimitPriceName });

  const positionAmount = Number(position?.amount) || 0;

  // ── Prefill on edit ──────────────────────────────────────────────────────
  // Same rule as the trigger-price prefill in useTPSLSync: only ever WRITE from
  // a live trigger, never clear from its absence (the list is briefly empty
  // whenever the executor is unreachable).
  const liveSource =
    triggers.take_profit?.priceSource || triggers.stop_loss?.priceSource;
  useEffect(() => {
    if (liveSource) {
      setPriceSource(liveSource);
    }
  }, [liveSource]);

  const liveSize =
    triggers.take_profit?.sizeInTokens || triggers.stop_loss?.sizeInTokens;
  useEffect(() => {
    if (positiveOrUndefined(liveSize)) {
      setConfigureAmount(true);
      setValue(closeSizeName, liveSize);
    }
  }, [liveSize, setValue]);

  const liveTpLimit = triggers.take_profit?.limitPrice;
  const liveSlLimit = triggers.stop_loss?.limitPrice;
  useEffect(() => {
    if (positiveOrUndefined(liveTpLimit) || positiveOrUndefined(liveSlLimit)) {
      setUseLimitPrice(true);
      if (positiveOrUndefined(liveTpLimit)) {
        setValue(tpLimitPriceName, liveTpLimit);
      }
      if (positiveOrUndefined(liveSlLimit)) {
        setValue(slLimitPriceName, liveSlLimit);
      }
    }
  }, [liveTpLimit, liveSlLimit, setValue]);

  // A closed modal must not keep a half-typed size around for the next
  // position — `reset()` clears the fields, this clears the switches.
  useEffect(() => {
    if (!show) {
      setConfigureAmount(false);
      setUseLimitPrice(false);
      setPriceSource(DEFAULT_TPSL_PRICE_SOURCE);
    }
  }, [show]);

  // ── Toggles ──────────────────────────────────────────────────────────────
  // `setValue` is a react-hook-form mutation, so it is called from the event
  // handler body, never from inside the updater. An updater runs during the
  // render phase and must be pure; this one wrote form state there, twice per
  // toggle under StrictMode. Reading the current flag from the closure is
  // correct here because the callback is recreated whenever it changes.
  const toggleConfigureAmount = useCallback(() => {
    // Default to the full position, which is exactly what the unchecked
    // behaviour does — so ticking the box alone never changes the order.
    if (!configureAmount && !positiveOrUndefined(closeSize) && positionAmount > 0) {
      setValue(closeSizeName, position?.amount, { shouldValidate: false });
    }
    setConfigureAmount((previous) => !previous);
  }, [configureAmount, closeSize, position?.amount, positionAmount, setValue]);

  const toggleUseLimitPrice = useCallback(() => {
    // Seed each enabled leg at its own trigger price: always inside the
    // executor's bound, and an obvious starting point to nudge.
    if (!useLimitPrice) {
      if (!positiveOrUndefined(tpLimitPrice) && positiveOrUndefined(tpTriggerPrice)) {
        setValue(tpLimitPriceName, tpTriggerPrice, { shouldValidate: false });
      }
      if (!positiveOrUndefined(slLimitPrice) && positiveOrUndefined(slTriggerPrice)) {
        setValue(slLimitPriceName, slTriggerPrice, { shouldValidate: false });
      }
    }
    setUseLimitPrice((previous) => !previous);
  }, [
    setValue,
    slLimitPrice,
    slTriggerPrice,
    tpLimitPrice,
    tpTriggerPrice,
    useLimitPrice,
  ]);

  /**
   * The 25/50/75/MAX affordance. The caller resolves the size exactly
   * (`tpslSizeForPercent`): MAX is the position itself, every other stop is
   * floored onto the market's grid. Nothing here re-reads it as a float —
   * `Number` of an 18-decimal size can land a wei above the position.
   */
  const setSizePercent = useCallback(
    (size: string) => {
      setValue(closeSizeName, size, { shouldValidate: true });
    },
    [setValue]
  );

  // ── What the submit path consumes ────────────────────────────────────────
  const desired = useMemo(
    () => ({
      priceSource,
      sizeInTokens: configureAmount
        ? closeSizeForSubmit(closeSize, position?.amount)
        : undefined,
      takeProfitLimitPrice: useLimitPrice
        ? positiveOrUndefined(tpLimitPrice)
        : undefined,
      stopLossLimitPrice: useLimitPrice
        ? positiveOrUndefined(slLimitPrice)
        : undefined,
    }),
    [
      closeSize,
      configureAmount,
      position?.amount,
      priceSource,
      slLimitPrice,
      tpLimitPrice,
      useLimitPrice,
    ]
  );

  return {
    configureAmount,
    toggleConfigureAmount,
    useLimitPrice,
    toggleUseLimitPrice,
    priceSource,
    setPriceSource,
    closeSize,
    closeSizeNum: Number(closeSize) || 0,
    positionAmount,
    /** The position's own amount text — exact, for the percentage stops. */
    positionAmountText: position?.amount,
    setSizePercent,
    desired,
  };
}

export type TPSLAdvanced = ReturnType<typeof useTPSLAdvanced>;
